import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import * as S from './PaymentMethodChanger.styles';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import {
  PaymentMethodChangeStatus,
  useErrorSummaryContext,
  usePaymentMethodContext,
} from '@contexts';
import { paymentMethodChangerUtils } from './paymentMethodChangerUtils';
import { useLoadingContext } from '../../App';
import { QueryParams } from '../../utils';
import {
  APPLE_PAY_REQUIRED,
  PAYMENT_METHOD_DATA,
  PaymentMethodData,
} from './PaymentMethodConstants';
import { PAYMENT_SUBMITTED_KEY } from '../layout';
import { useExperiment } from '../../contexts/experiment/useExperiment';
import { METHOD_BADGE_EXPERIMENT, shouldShowMethodBadge } from './methodBadge';

export interface AvailablePaymentMethod {
  shortCode: string;
  name: string;
  methodGroupId: number;
  internalData: PaymentMethodData[string];
  providerCode?: string;
  internalName?: React.ReactNode;
  isAvailable?: boolean;
  availabilityMessage?: string;
  order?: number;
  badgeCode?: string;
}

interface PaymentMethodChangerProps {
  clientSideData?: {
    chosenPaymentMethod: AvailablePaymentMethod;
    availablePaymentMethods: AvailablePaymentMethod[];
  };
  queryParams: QueryParams;
  setErrorMessage: (errorMessage?: string) => void;
}

const PaymentMethodChanger = ({
  clientSideData,
  queryParams,
  setErrorMessage,
}: PaymentMethodChangerProps) => {
  const { t } = useTranslation(['npp']);
  const location = useLocation();
  const navigate = useNavigate();
  const { clearErrorSummary } = useErrorSummaryContext();
  const [availabilityNotification, setAvailabilityNotification] = useState<string | null>(null);
  const availabilityNotificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    status,
    nextChangeAllowed,
    chosenPaymentMethod,
    availablePaymentMethods,
    forcePaymentMethodChangerDisabled,
    setPaymentMethod,
  } = usePaymentMethodContext();
  const { isLoading } = useLoadingContext();
  const methodBadgeVariant = useExperiment(METHOD_BADGE_EXPERIMENT);

  const paymentMethodData = useMemo(() => {
    if (clientSideData) {
      return clientSideData;
    }

    return { chosenPaymentMethod, availablePaymentMethods };
  }, [clientSideData, availablePaymentMethods, chosenPaymentMethod]);

  // A Popular badge renders only under the badge-on treatment, for a method that
  // has a BadgeCode AND is currently available (don't promote a method the user
  // can't pick). Single source of truth for the per-tile badge AND the bundled
  // tile-enlargement gate.
  const isBadgeVisible = useCallback(
    (pm: AvailablePaymentMethod) =>
      shouldShowMethodBadge(methodBadgeVariant, pm.badgeCode) && (pm.isAvailable ?? true),
    [methodBadgeVariant]
  );

  // Enlarged tile height is part of the treatment: tiles grow (uniformly, to keep
  // the grid aligned) only when a Popular badge actually renders.
  const hasMethodBadge = useMemo(
    () => paymentMethodData.availablePaymentMethods.some(isBadgeVisible),
    [paymentMethodData.availablePaymentMethods, isBadgeVisible]
  );

  const selectingPaymentMethod = location.pathname === '/payment';
  const isGloballyDisabled =
    isLoading || status === PaymentMethodChangeStatus.Loading || !nextChangeAllowed;

  const onSelect = (paymentMethod: AvailablePaymentMethod) => {
    if (forcePaymentMethodChangerDisabled || isGloballyDisabled) return;

    if (!paymentMethod.isAvailable) {
      if (paymentMethod.availabilityMessage) {
        const translatedMessage = getTranslatedAvailabilityMessage(
          paymentMethod.availabilityMessage
        );
        setAvailabilityNotification(translatedMessage);

        if (availabilityNotificationTimeoutRef.current) {
          clearTimeout(availabilityNotificationTimeoutRef.current);
        }
        availabilityNotificationTimeoutRef.current = setTimeout(
          () => setAvailabilityNotification(null),
          3000
        );
      }
      return;
    }

    const isSameMethod =
      paymentMethodData.chosenPaymentMethod?.methodGroupId === paymentMethod.methodGroupId;
    if (isSameMethod) return;

    onPaymentMethodChange(paymentMethod);
  };

  const onPaymentMethodChange = useCallback(
    (paymentMethod: AvailablePaymentMethod) => {
      setErrorMessage(undefined);
      setPaymentMethod(paymentMethod);
      clearErrorSummary();
      sessionStorage.removeItem(PAYMENT_SUBMITTED_KEY(queryParams.getPaymentId()));
    },
    [setErrorMessage, setPaymentMethod, clearErrorSummary, queryParams]
  );

  const getPaymentMethodTitle = useCallback(
    (paymentMethod: AvailablePaymentMethod) => {
      const translationKey =
        PAYMENT_METHOD_DATA[paymentMethod.shortCode.toLowerCase()]?.translationKey;
      return translationKey ? t(translationKey, paymentMethod.name) : paymentMethod.name;
    },
    [t]
  );

  const getTranslatedAvailabilityMessage = useCallback(
    (availabilityMessage: string | undefined) => {
      if (!availabilityMessage) return null;
      if (availabilityMessage === APPLE_PAY_REQUIRED) {
        return t('npp:paymentMethodChanger.browserRequirements.unavailableOnDeviceWithoutApplePay');
      }
      return availabilityMessage;
    },
    [t]
  );

  const maxTextLength = useMemo(() => {
    const paymentMethodTitleLengths = paymentMethodData.availablePaymentMethods.map((pm) => {
      const title = getPaymentMethodTitle(pm) || '';
      return String(title).length;
    });

    return Math.max(...paymentMethodTitleLengths);
  }, [paymentMethodData.availablePaymentMethods, getPaymentMethodTitle]);

  useEffect(() => {
    if (!chosenPaymentMethod || nextChangeAllowed) return;

    const validUrl = paymentMethodChangerUtils.generateUrlForPaymentMethod(
      queryParams,
      chosenPaymentMethod
    );
    const isCorrectPath = paymentMethodChangerUtils.isPathCorrectForPaymentMethod(
      location.pathname,
      chosenPaymentMethod
    );

    if (!isCorrectPath) {
      navigate(validUrl, { replace: true });
    }
  }, [chosenPaymentMethod, location, navigate, nextChangeAllowed, queryParams]);

  useEffect(() => {
    return () => {
      if (availabilityNotificationTimeoutRef.current) {
        clearTimeout(availabilityNotificationTimeoutRef.current);
      }
    };
  }, []);

  return (
    <S.Root>
      <h4>
        {selectingPaymentMethod
          ? t('npp:paymentMethodChanger.cta')
          : t('npp:paymentMethodChanger.title')}
      </h4>

      <S.PaymentMethodList className="row">
        {paymentMethodData.availablePaymentMethods.map((pm) => {
          const isBlocked = !(pm.isAvailable ?? true);
          const isCurrentMethod =
            pm.methodGroupId === paymentMethodData.chosenPaymentMethod?.methodGroupId;
          const isDisabled =
            forcePaymentMethodChangerDisabled ||
            (isGloballyDisabled && !isCurrentMethod) ||
            isBlocked;

          const showMethodBadge = isBadgeVisible(pm);
          const methodBadgeText = t(`npp:paymentMethodChanger.${pm.badgeCode}`, 'Popular');

          const baseTitle =
            isBlocked && pm.availabilityMessage
              ? `${getPaymentMethodTitle(pm)} - ${getTranslatedAvailabilityMessage(pm.availabilityMessage)}`
              : getPaymentMethodTitle(pm);
          const title = showMethodBadge ? `${baseTitle} - ${methodBadgeText}` : baseTitle;

          return (
            <div className="col-4" key={pm.shortCode} title={title}>
              <S.PaymentMethodSegment
                role="button"
                aria-disabled={isDisabled}
                onKeyDown={(e) => e.key === 'Enter' && onSelect(pm)}
                selectable
                tabIndex={isDisabled ? undefined : 0}
                selected={isCurrentMethod}
                data-selected={isCurrentMethod}
                onClick={() => onSelect(pm)}
                disabled={isDisabled}
                data-test-id={`pm-${pm.shortCode}`}
                data-provider-code={pm.providerCode?.toLowerCase()}
                textLength={maxTextLength}
                badgeRoom={hasMethodBadge}
              >
                {showMethodBadge && (
                  <S.MethodBadge data-test-id={`pm-method-badge-${pm.shortCode}`}>
                    {methodBadgeText}
                  </S.MethodBadge>
                )}
                {pm.internalData.withBrandedLogo ? (
                  <S.PaymentMethodLogo>{pm.internalData.img}</S.PaymentMethodLogo>
                ) : (
                  <S.InternalPaymentMethodWrapper>
                    <S.InternalPaymentMethodLogo>{pm.internalData.img}</S.InternalPaymentMethodLogo>
                    <S.InternalPaymentMethodText>{pm.internalName}</S.InternalPaymentMethodText>
                  </S.InternalPaymentMethodWrapper>
                )}
              </S.PaymentMethodSegment>
            </div>
          );
        })}
      </S.PaymentMethodList>
      {!nextChangeAllowed && paymentMethodData.availablePaymentMethods.length > 1 && (
        <S.PaymentMethodChangerMessage
          variant="warning"
          header={t('npp:paymentMethodChanger.blockedTitle')}
        >
          {t('npp:paymentMethodChanger.blockedMax')}
        </S.PaymentMethodChangerMessage>
      )}

      {availabilityNotification && (
        <S.PaymentMethodChangerMessage
          variant="info"
          header={t('npp:paymentMethodChanger.unavailableTitle')}
        >
          {availabilityNotification}
        </S.PaymentMethodChangerMessage>
      )}
    </S.Root>
  );
};

export default PaymentMethodChanger;
